import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Col, Flex, Form, message } from 'antd';
import {
  CustomButton,
  CustomSelect,
  CustomTitle,
} from '../../../../components';
import { useToken } from '../../../../common/helper';
import { useSpin } from '../../../../components/SpinContext.jsx';
import { copyTeamsSettings } from '../../../../apis/settings';
import { unAuth } from '../../../../common/utils/unauth';
import CustomDeleteModal from '../../../Modals/CustomDeleteModal';
import { themeT } from '@/utils/themeText';
import theme from '@/assets/theme';

// Type definitions
interface TeamOption {
  value?: string;
  label?: string;
}

interface FormValues {
  source_team_id?: string;
  destination_team_id?: string;
}

interface AlertCopySettingsFormProps {
  teamsOptions: TeamOption[];
  closeModal: () => void;
  onSuccess: () => void;
}

/**
 * AlertCopySettingsForm Component
 *
 * Form component for copying alert settings from one team to another.
 * Includes validation, confirmation modal, and API integration.
 *
 * @param teamsOptions - Array of available team options
 * @param closeModal - Function to close the modal
 * @param onSuccess - Callback function called after successful copy operation
 */
const AlertCopySettingsForm: React.FC<AlertCopySettingsFormProps> = ({
  teamsOptions,
  closeModal,
  onSuccess,
}) => {
  const navigate = useNavigate();
  const [form] = Form.useForm<FormValues>();
  const { token, removeToken } = useToken();
  const { showSpin, hideSpin } = useSpin();

  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [formValues, setFormValues] = useState<FormValues>({});
  const [selectedTeam, setSelectedTeam] = useState<TeamOption>({});

  const team_without_All = teamsOptions?.filter(
    (team) => team?.value !== 'all'
  );
  const onFinish = (values: FormValues) => {
    if (values && Object.keys(values).length > 0) {
      const model: FormValues = {
        ...values,
      };
      const foundTeam = team_without_All.find(
        (team) => team?.value === values?.destination_team_id
      );
      if (foundTeam) {
        setSelectedTeam(foundTeam);
      }
      setFormValues(model);
      setShowDeleteModal(true);
    }
  };

  const handleSubmitCopySettings = (model: FormValues) => {
    showSpin();
    copyTeamsSettings(token ?? '', model)
      .then((res) => {
        const data = res?.data || null;
        if (data) {
          if (data?.data?.is_success) {
            message.success(data?.data?.message);
            form.resetFields();
            setShowDeleteModal(false);
            setFormValues({});
            onSuccess();
          } else {
            message.error(data?.data?.message);
          }
        }
      })
      .catch((error) => {
        console.error(error);
        unAuth(error, navigate, removeToken);
      })
      .finally(() => {
        hideSpin();
      });
  };

  const handleConfirmCopy = () => {
    handleSubmitCopySettings(formValues);
    setShowDeleteModal(true);
  };

  return (
    <>
      <Form form={form} layout='vertical' onFinish={onFinish}>
        <Flex gap={16} vertical>
          <Col xs={24} sm={24} md={11}>
            <CustomTitle
              text='Copy from'
              fontSize='14px'
              fontWeight='600'
              lineHeight='16px'
              color='var(--text-primary)'
              marginBottom='4px'
            />
            <Form.Item
              name='source_team_id'
              rules={[
                {
                  required: true,
                  message: `Please select a ${themeT('team', theme.themeType)?.toLowerCase()} to copy from.`,
                },
              ]}
            >
              <CustomSelect
                placeholder='Select'
                lineHeight='16px'
                border='0px'
                bgColor='var(--light-gray)'
                borderRadius={'6px'}
                boxShadow={'none'}
                height='36px'
                width='100%'
                options={team_without_All}
              />
            </Form.Item>
          </Col>
          <Col xs={24} sm={24} md={11}>
            <CustomTitle
              text='Copy to'
              fontSize='14px'
              fontWeight='600'
              lineHeight='16px'
              color='var(--text-primary)'
              marginBottom='4px'
            />
            <Form.Item
              name='destination_team_id'
              rules={[
                {
                  required: true,
                  message: `Please select a ${themeT('team', theme.themeType)?.toLowerCase()} to copy to`,
                },
              ]}
            >
              <CustomSelect
                placeholder='Select'
                lineHeight={'16px'}
                border='0px'
                bgColor='var(--light-gray)'
                borderRadius={'6px'}
                boxShadow={'none'}
                height='36px'
                width='100%'
                options={team_without_All}
              />
            </Form.Item>
          </Col>
        </Flex>

        <Flex
          style={{
            position: 'sticky',
            bottom: '0',
            paddingTop: '28px',
          }}
          gap={8}
          justify='flex-end'
        >
          <CustomButton
            text='Cancel'
            onClick={closeModal}
            width='72px'
            height='36px'
            bgColor='var(--surface-container)'
            color='var(--text-primary)'
            fontSize='14px'
            fontWeight={600}
            borderRadius='8px'
            boxShadow={'none'}
          />
          <CustomButton
            text='Duplicate'
            htmlType='submit'
            width='89px'
            height='36px'
            bgColor='#000'
            color='#fff'
            fontSize='14px'
            fontWeight={600}
            borderRadius='8px'
            boxShadow='0px 4px 16px 0px rgba(0, 0, 0, 0.27)'
          />
        </Flex>
      </Form>

      <CustomDeleteModal
        visible={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={() => handleConfirmCopy()}
        title={`Confirm duplication`}
        message={`Duplicated settings will override existing settings for ‘${selectedTeam?.label}’ ${themeT('team', theme.themeType).toLowerCase()}. This cannot be undone.`}
        cancelText={'Cancel'}
        confirmText={'Confirm'}
        confirmColor={`#000`}
      />
    </>
  );
};

export default AlertCopySettingsForm;
